/**
 * Glitch on/off toggle. Clear boolean switch: OFF = dark muted, ON = luster-colored track.
 * variant "inline" = compact row for ActionSidebar dropdown.
 * variant "button" = ModalActionButton expand-left pill (legacy pages).
 */
import { useGlitchEnabled } from "../lib/useGlitchEnabled"
import { ModalActionButton } from "./ModalActionButton"
import { Zap } from "lucide-react"

export interface GlitchSwitchProps {
  variant?: "button" | "inline"
}

function GlitchToggleTrack({ enabled, onClick, ariaLabel }: { enabled: boolean; onClick: () => void; ariaLabel: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={ariaLabel}
      onClick={onClick}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 ${
        enabled
          ? "bg-luster-400/30 border-luster-400/50"
          : "bg-white/5 border-white/15"
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-4 w-4 rounded-full shadow-sm transition-all duration-200 mt-0.5 ${
          enabled
            ? "translate-x-[22px] bg-luster-400"
            : "translate-x-[3px] bg-white/40"
        }`}
        aria-hidden
      />
    </button>
  )
}

export function GlitchSwitch({ variant = "button" }: GlitchSwitchProps) {
  const [enabled, setEnabled] = useGlitchEnabled()
  const toggle = () => setEnabled(!enabled)
  const ariaLabel = enabled ? "Turn off glitch mode" : "Turn on glitch mode"

  if (variant === "inline") {
    return (
      <div
        role="group"
        aria-label="Glitch effects"
        className="flex items-center justify-between gap-3 rounded-lg bg-white/5 border border-transparent px-2.5 py-2 w-full"
      >
        <div className="flex items-center gap-2">
          <Zap className={`w-3.5 h-3.5 shrink-0 ${enabled ? "text-luster-400" : "text-white/40"}`} />
          <span className={`text-[10px] font-bold uppercase tracking-widest whitespace-nowrap ${enabled ? "text-white/80" : "text-white/40"}`}>
            Glitch {enabled ? "on" : "off"}
          </span>
        </div>
        <GlitchToggleTrack enabled={enabled} onClick={toggle} ariaLabel={ariaLabel} />
      </div>
    )
  }

  return (
    <ModalActionButton
      icon={Zap}
      label={`Glitch ${enabled ? "on" : "off"}`}
      explainer={ariaLabel}
      onClick={toggle}
      aria-label={ariaLabel}
    >
      <Zap className={`w-4 h-4 ${enabled ? "text-luster-400" : "text-white/50"}`} />
    </ModalActionButton>
  )
}
